import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import DataTable from "../../../DataTable";

import useOutputReportStore from "../../../../store/OutputReport.store";
import {
  formatDataTableColumns,
  generateFootnote,
  getOutputType,
} from "../../../../utils/helpers/dataTable";
import {
  dataTableCopy,
  liveAccountsCopy,
} from "../../../../utils/resources/dataTableCopy";

const defaultTableRowKeys = [
  {
    label: "Number of Securities",
    key: "Number of Securities",
  },
  {
    label: "Beta to Index",
    key: "Beta to Index",
  },
  {
    label: "Effective Duration",
    key: "Effective Duration (OAD)",
  },
  {
    label: "Option Adjusted Spread (OAS)",
    key: "Option Adjusted Spread (OAS)",
  },
  {
    label: "Option Adjusted Spread Duration (OASD)",
    key: "Option Adjusted Spread Duration (OASD)",
  },
  {
    label: "Duration Times Spread (DTS)",
    key: "Duration Times Spread (DTS)",
  },
  {
    label: "Time to Maturity",
    key: "Time to Maturity",
  },
  {
    label: "Coupon",
    key: "Coupon",
  },
  {
    label: "Yield to Maturity[3]",
    key: "Yield to Maturity [3]",
  },
  {
    label: "Yield to Worst[3]",
    key: "Yield to Worst [3]",
  },
  {
    label: "Yield to Worst[4]",
    key: "Yield to Worst [4]",
  },
  {
    label: "Credit Quality",
    key: "Credit Quality",
  },
  {
    label: "Price",
    key: "Price",
  },
  {
    label: "% Callable",
    key: "% Callable",
  },
  {
    label: "Coupon Income (Next 12 months)[4]",
    key: "Coupon Income (Next 12 months) [4]",
  },
  {
    label: "Liquidity Score (LQA)",
    key: "Liquidity Score (LQA)",
  },
  {
    label: "ESG Rating",
    key: "ESG Rating",
  },
  {
    label: "Tracking Error",
    key: "Ending Tracking Error",
  },
  {
    label: "Number of Securities[3]",
    key: "Number of Securities****",
  },
  {
    label: "Tracking Error to Benchmark",
    key: "Tracking Error to Benchmark",
  },
  {
    label: "% of Market Value in Stocks",
    key: "% of Market Value in Stocks",
  },
  {
    label: "Beta to Benchmark",
    key: "Beta to Benchmark",
  },
  {
    label: "Dividend Yield",
    key: "Dividend Yield",
  },
  {
    label: "Weight in Top Ten Holdings (%)",
    key: "Weight in Top Ten Holdings (%)",
  },
  {
    label: "Average Weighted Market Cap (Billions $)[4]",
    key: "Average Weighted Market Cap (Billions $)****",
  },
  {
    label: "Large Cap (%)[4]",
    key: "Large Cap (%)****",
  },
  {
    label: "Mid Cap (%)[4]",
    key: "Mid Cap (%)****",
  },
  {
    label: "Small Cap (%)[4]",
    key: "Small Cap (%)****",
  },
  {
    label: "Defensive Sectors (%)[4]",
    key: "Defensive Sectors (%)****",
  },
  {
    label: "Highly Cyclical Sectors (%)[4]",
    key: "Highly Cyclical Sectors (%)****",
  },
  {
    label: "Moderately Cyclical Sectors (%)[4]",
    key: "Moderately Cyclical Sectors (%)****",
  },
];

const getFootnote = ({ reportType, assetType }) => {
  if (assetType === "equity") {
    reportType === "cash" ? (
      <>
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.targetPortfolios}
        <br />
        [2]{dataTableCopy.seeBenchmark}
        <br />
        [3]{dataTableCopy.calculationExcludes}
        <br />
        [4]{dataTableCopy.statsExcludeExposure}
      </>
    ) : (
      <>
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        {dataTableCopy.existingPortfolioInformation}
        <br />
        {dataTableCopy.transitionAnalysisDiscussed}
        <br />
        {dataTableCopy.seeBenchmark}
        <br />
        {dataTableCopy.calculationExcludes}
        <br />
        {dataTableCopy.statsExcludeExposure}
      </>
    );
  }

  if (reportType === "transition") {
    return (
      <>
        [1]{dataTableCopy.existingPortfolioInformation}
        <br />
        [2]{dataTableCopy.modelAndTransitionAnalysis}
        <br />
        [3]{dataTableCopy.seeReferenceIndex}
        <br />
        [4]{dataTableCopy.yieldsMarketCondition}
        <br />
        [5]{dataTableCopy.couponCashFlowCalculated}
      </>
    );
  }
  const cashNotes = [
    "targetPortfolios",
    "seeReferenceIndex",
    "yieldsMarketCondition",
    "couponCashFlowCalculated",
  ];

  if (reportType === "cash") {
    return (
      <>
        {dataTableCopy.unlessOtherwiseStated}
        <br /> {generateFootnote(cashNotes, dataTableCopy)}
      </>
    );
  }

  return (
    <>
      *{dataTableCopy.proposalNotMaintained}
      <br />
      {liveAccountsCopy[assetType]}
      <br />
      {dataTableCopy.cashExcludedTimeMaturity}
      <br />
      ****{dataTableCopy.seeReferenceIndex}
    </>
  );
};

const PortfolioCharacteristicsDataTable = ({ name, visibleRows }) => {
  const reportName = "Portfolio Characteristics";
  const [tableRowKeys, setTableRowKeys] = useState(defaultTableRowKeys);
  const data = useOutputReportStore((state) => state.data);
  const types = useOutputReportStore((state) => state.types);
  const { reportType, assetType } = getOutputType(data);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (visibleRows.length) {
      if (
        reportType === "transition" &&
        (assetType === "corp" || assetType === "muni")
      ) {
        const filteredRows = defaultTableRowKeys.filter(({ key }) => {
          return visibleRows.includes(key);
        });
        return setTableRowKeys(
          filteredRows.map(({ key, label }) => {
            if (key === "Yield to Maturity") {
              key = "Yield to Maturity [4]";
              label = "Yield to Maturity[4]";
            }
            if (key === "Yield to Worst [3]") {
              key = "Yield to Worst [4]";
              label = "Yield to Worst[4]";
            }
            if (key === "Coupon Income (Next 12 months) [4]") {
              key = "Coupon Income (Next 12 months) [5]";
              label = "Coupon Income (Next 12 months)[5]";
            }
            return { key, label };
          })
        );
      }
      setTableRowKeys(
        defaultTableRowKeys.filter(({ key }) => {
          return visibleRows.includes(key);
        })
      );
    }
  }, [assetType, reportType, visibleRows]);

  // TODO: Remove the block below after API removes the Reference Index***.
  let tableColumns = formatDataTableColumns(reportName, data);
  if (assetType === "equity") {
    tableColumns = tableColumns.filter(
      (item) => item.columnTitle !== "Reference Index***"
    );
  }

  return (
    <DataTable
      reportType={reportType}
      name={name || "portfolioCharacteristics"}
      variation="stylized"
      tableCaption={`${reportName} Data Table`}
      tableRowHeaderKey={reportName}
      tableRowKeys={tableRowKeys}
      tableColumns={tableColumns}
      tableDataTypes={types?.[reportName]}
      formatCellDataBy="row"
      footnote={getFootnote({ reportType, assetType })}
    />
  );
};

PortfolioCharacteristicsDataTable.propTypes = {
  name: PropTypes.string,
  visibleRows: PropTypes.array,
};

PortfolioCharacteristicsDataTable.defaultProps = {
  visibleRows: [],
};

export default PortfolioCharacteristicsDataTable;
